import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { Text } from 'tio-ui/components/typography';
import { PhCaretRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for the action link card
 */
export type ActionLinkColor =
  | 'violet'
  | 'indigo'
  | 'teal'
  | 'emerald'
  | 'blue'
  | 'amber'
  | 'pink'
  | 'green';

/**
 * ActionLinkCard - A horizontal navigation card with icon, title, subtitle, and caret
 *
 * Used for prominent navigation actions in dashboard sections.
 * Features colored icon badge, hover effects, and caret indicator.
 *
 * @example
 * ```gts
 * <ActionLinkCard
 *   @route="authenticated.tas.dashboard"
 *   @icon={{PhPlus}}
 *   @title="Start New Application"
 *   @subtitle="Request education funding"
 *   @color="indigo"
 * />
 * ```
 */
interface ActionLinkCardSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Subtitle text */
    subtitle?: string;
    /** Color theme for icon and hover effects */
    color?: ActionLinkColor;
  };
  Element: HTMLAnchorElement;
}

const colorClasses: Record<
  ActionLinkColor,
  { bg: string; text: string; hoverBg: string; hoverBorder: string; hoverCaret: string }
> = {
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/50',
    text: 'text-violet-600 dark:text-violet-400',
    hoverBg: 'group-hover:bg-violet-200 dark:group-hover:bg-violet-800/50',
    hoverBorder: 'hover:border-violet-200 dark:hover:border-violet-700',
    hoverCaret: 'group-hover:text-violet-600 dark:group-hover:text-violet-400',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/50',
    text: 'text-indigo-600 dark:text-indigo-400',
    hoverBg: 'group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/50',
    hoverBorder: 'hover:border-indigo-200 dark:hover:border-indigo-700',
    hoverCaret: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
  },
  teal: {
    bg: 'bg-teal-100 dark:bg-teal-900/50',
    text: 'text-teal-600 dark:text-teal-400',
    hoverBg: 'group-hover:bg-teal-200 dark:group-hover:bg-teal-800/50',
    hoverBorder: 'hover:border-teal-200 dark:hover:border-teal-700',
    hoverCaret: 'group-hover:text-teal-600 dark:group-hover:text-teal-400',
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/50',
    text: 'text-emerald-600 dark:text-emerald-400',
    hoverBg: 'group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800/50',
    hoverBorder: 'hover:border-emerald-200 dark:hover:border-emerald-700',
    hoverCaret: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/50',
    text: 'text-green-600 dark:text-green-400',
    hoverBg: 'group-hover:bg-green-200 dark:group-hover:bg-green-800/50',
    hoverBorder: 'hover:border-green-200 dark:hover:border-green-700',
    hoverCaret: 'group-hover:text-green-600 dark:group-hover:text-green-400',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/50',
    text: 'text-blue-600 dark:text-blue-400',
    hoverBg: 'group-hover:bg-blue-200 dark:group-hover:bg-blue-800/50',
    hoverBorder: 'hover:border-blue-200 dark:hover:border-blue-700',
    hoverCaret: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/50',
    text: 'text-amber-600 dark:text-amber-400',
    hoverBg: 'group-hover:bg-amber-200 dark:group-hover:bg-amber-800/50',
    hoverBorder: 'hover:border-amber-200 dark:hover:border-amber-700',
    hoverCaret: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/50',
    text: 'text-pink-600 dark:text-pink-400',
    hoverBg: 'group-hover:bg-pink-200 dark:group-hover:bg-pink-800/50',
    hoverBorder: 'hover:border-pink-200 dark:hover:border-pink-700',
    hoverCaret: 'group-hover:text-pink-600 dark:group-hover:text-pink-400',
  },
};

const getColorClasses = (color: ActionLinkColor = 'violet') => colorClasses[color];

const ActionLinkCard: TOC<ActionLinkCardSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <LinkTo
      @route={{@route}}
      class="flex items-center gap-4 p-5 bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md
        {{colors.hoverBorder}}
        transition-all group"
      ...attributes
    >
      <div
        class="p-3 {{colors.bg}} rounded-xl {{colors.text}} {{colors.hoverBg}} transition-colors"
      >
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} />
        {{/let}}
      </div>
      <div class="flex-1">
        <div class="font-semibold text-gray-900 dark:text-white">{{@title}}</div>
        {{#if @subtitle}}
          <Text @intent="muted" class="text-sm">{{@subtitle}}</Text>
        {{/if}}
      </div>
      <PhCaretRight @size={{20}} class="text-gray-400 dark:text-gray-500 {{colors.hoverCaret}}" />
    </LinkTo>
  {{/let}}
</template>;

export default ActionLinkCard;
